/**
 * NeuralNetGraph - a pannable / zoomable, topology-accurate view of a
 * neural-network blueprint.
 *
 * This is intentionally NOT a generic "lots of dots" schematic. It
 * renders the architecture as it will actually be trained:
 *
 *   - Node count per column = the layer's TRUE width (units / filters),
 *     capped for legibility with an explicit ellipsis + the exact count
 *     labeled, so a 256-unit layer reads as "256" and a 1-unit output
 *     reads as a single node.
 *   - The synthetic INPUT column uses the real feature count of the
 *     chosen segment (when known), and a synthetic OUTPUT head shows the
 *     real (forecast_horizon x num_labels) projection that the trainer
 *     auto-appends to every blueprint (see the per-framework
 *     model_builder). So the picture matches the model that trains.
 *   - Recurrent layers (LSTM / GRU / RNN / Bidirectional) draw a
 *     self-loop on each node to show temporal recurrence, and tag
 *     "returns sequences" when set.
 *   - Dropout is drawn by hollowing out ~rate of the previous layer's
 *     nodes (and fading their edges) - a literal read of what dropout
 *     does at train time.
 *   - Normalization / activation / pooling / flatten / reshape attach as
 *     labeled modifiers on the column they follow (they don't change the
 *     channel width, so they aren't separate node columns).
 *   - Per-layer parameter counts are estimated and shown in the tooltip;
 *     the total is badged in the corner.
 *
 * Connectivity between adjacent columns is dense, which is the correct
 * read in channel space: Dense, recurrent and Conv1D layers all mix
 * every input channel into every output channel (Conv's locality is over
 * the time axis, which this channel-width view doesn't draw). Edge
 * weight/opacity are seeded from a deterministic PRNG so the picture is
 * stable per blueprint but still organic.
 *
 * Dependency-free (React + SVG + MUI primitives).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

// --- layout constants --------------------------------------------------------
const NODE_R = 7;
const NODE_GAP = 32;
const COL_GAP = 210;
const RENDER_CAP = 13;     // max nodes drawn per column (odd -> clean middle ellipsis)
const PAD = 96;
const MIN_K = 0.2;
const MAX_K = 4;

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

// --- layer taxonomy ----------------------------------------------------------
// Columns that have a real channel width -> drawn as a node column.
const DIM_TYPES = new Set([
  'dense', 'output', 'lstm', 'gru', 'rnn', 'bidirectional',
  'conv1d', 'dilatedconv1d', 'embedding', 'timedistributed',
]);
const RECURRENT_TYPES = new Set(['lstm', 'gru', 'rnn', 'bidirectional']);
// Modifiers attach to the preceding column instead of getting their own.
const MODIFIER_LABELS = {
  dropout: 'dropout',
  batchnormalization: 'batch norm',
  layernormalization: 'layer norm',
  activation: 'activation',
  masking: 'masking',
  maxpooling1d: '↓ max pool',
  averagepooling1d: '↓ avg pool',
  globalmaxpooling1d: '↓ global max',
  globalaveragepooling1d: '↓ global avg',
  flatten: 'flatten',
  reshape: 'reshape',
  permute: 'permute',
};

const normType = (t) => String(t || '').toLowerCase().replace(/\s+/g, '');

// --- deterministic PRNG / color helpers -------------------------------------
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};
const hashString = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
};
const hexToRgb = (hex) => {
  const h = String(hex || '#888888').replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const rgbToHex = ({ r, g, b }) =>
  '#' + [r, g, b].map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0')).join('');
const lerpColor = (a, b, t) => {
  const ca = hexToRgb(a); const cb = hexToRgb(b);
  return rgbToHex({ r: ca.r + (cb.r - ca.r) * t, g: ca.g + (cb.g - ca.g) * t, b: ca.b + (cb.b - ca.b) * t });
};
const layerColor = (theme, type) => theme.nodeColors?.[normType(type)] || theme.palette.secondary.main;

const fmtNum = (p) => {
  if (p == null) return null;
  if (p >= 1e9) return `${(p / 1e9).toFixed(1)}B`;
  if (p >= 1e6) return `${(p / 1e6).toFixed(1)}M`;
  if (p >= 1e3) return `${(p / 1e3).toFixed(1)}K`;
  return String(Math.round(p));
};

// Estimate trainable params for a layer given the incoming channel width.
const estimateParams = (type, inW, outW, layer) => {
  const t = normType(type);
  switch (t) {
    case 'dense': case 'output': case 'timedistributed':
      return inW * outW + outW;
    case 'lstm': case 'bidirectional': {
      const base = 4 * ((inW + outW) * outW + outW);
      return t === 'bidirectional' ? base * 2 : base;
    }
    case 'gru':
      return 3 * ((inW + outW) * outW + outW);
    case 'rnn':
      return (inW + outW) * outW + outW;
    case 'conv1d': case 'dilatedconv1d': {
      const k = Number(layer.kernel_size) || 3;
      return outW * (inW * k + 1);
    }
    case 'embedding': {
      const inDim = Number(layer.input_dim) || 1024;
      return inDim * outW;
    }
    default:
      return 0;
  }
};

const widthOf = (layer) => {
  const w = layer.units ?? layer.filters;
  return w == null ? null : Number(w);
};

// Build the column model + total params from the layer list.
const buildArchitecture = (theme, layers, opts) => {
  const { inputSize, outputLabels, forecastHorizon } = opts;
  const cols = [];
  let totalParams = 0;

  // INPUT column (real feature count when known).
  const inW = inputSize && inputSize > 0 ? inputSize : 6;
  cols.push({
    key: 'input',
    type: 'input',
    title: 'Input',
    sub: inputSize ? `${inputSize} feature${inputSize === 1 ? '' : 's'}` : 'features',
    color: layerColor(theme, 'input'),
    trueCount: inW,
    recurrent: false,
    modifiers: [],
    dropRate: 0,
    params: 0,
    synthetic: true,
  });

  let width = inW;
  (layers || []).forEach((layer, i) => {
    const t = normType(layer.type);

    // Modifier? attach to the last real column.
    if (!DIM_TYPES.has(t)) {
      const last = cols[cols.length - 1];
      if (t === 'dropout') {
        const rate = Number(layer.rate ?? layer.dropout ?? 0) || 0;
        last.modifiers.push({ kind: 'dropout', label: `${MODIFIER_LABELS.dropout} ${rate}` });
        last.dropRate = Math.max(last.dropRate, rate);
      } else if (t === 'activation') {
        last.modifiers.push({ kind: 'act', label: layer.activation || MODIFIER_LABELS.activation });
      } else {
        last.modifiers.push({ kind: 'mod', label: MODIFIER_LABELS[t] || t });
      }
      return;
    }

    // Dimension-producing layer -> its own column.
    const w = widthOf(layer) ?? width;
    const params = estimateParams(layer.type, width, w, layer);
    totalParams += params;

    const subBits = [];
    if (widthOf(layer) != null) subBits.push(`${widthOf(layer)} ${layer.filters != null ? 'filters' : 'units'}`);
    if (layer.kernel_size != null) subBits.push(`k=${layer.kernel_size}`);
    if (layer.activation) subBits.push(layer.activation);
    if (layer.return_sequences === true) subBits.push('returns sequences');

    cols.push({
      key: `l${i}`,
      type: layer.type,
      title: layer.type || 'Layer',
      sub: subBits.join(' · '),
      color: layerColor(theme, layer.type),
      trueCount: Math.max(1, w),
      recurrent: RECURRENT_TYPES.has(t),
      returnsSeq: layer.return_sequences === true,
      modifiers: [],
      dropRate: 0,
      params,
      synthetic: false,
    });
    width = w;
  });

  // OUTPUT head (the trainer always workspaces to horizon x labels).
  if (forecastHorizon && forecastHorizon > 0) {
    const labels = outputLabels && outputLabels > 0 ? outputLabels : 1;
    const outN = forecastHorizon * labels;
    const params = width * outN + outN;
    totalParams += params;
    cols.push({
      key: 'output',
      type: 'output',
      title: 'Output',
      sub: labels > 1 ? `${forecastHorizon}×${labels} = ${outN}` : `${forecastHorizon} step${forecastHorizon === 1 ? '' : 's'}`,
      color: layerColor(theme, 'output'),
      trueCount: outN,
      recurrent: false,
      modifiers: [],
      dropRate: 0,
      params,
      synthetic: true,
    });
  }

  return { cols, totalParams };
};

// Lay out node positions (with ellipsis for truncated columns) + edges.
const buildScene = (cols, seed) => {
  const rand = mulberry32(seed);

  const laid = cols.map((col) => {
    const truncated = col.trueCount > RENDER_CAP;
    const render = truncated ? RENDER_CAP : col.trueCount;
    const midEllipsis = truncated ? Math.floor(render / 2) : -1;

    // dropout mask: hollow ~dropRate of the (non-ellipsis) nodes, evenly spread.
    const dropCount = col.dropRate > 0 ? Math.round(col.dropRate * render) : 0;
    const dropped = new Set();
    if (dropCount > 0) {
      const step = render / dropCount;
      for (let d = 0; d < dropCount; d += 1) {
        const idx = Math.min(render - 1, Math.round(d * step + step / 2));
        if (idx !== midEllipsis) dropped.add(idx);
      }
    }
    return { ...col, render, truncated, midEllipsis, dropped };
  });

  const maxRender = Math.max(...laid.map((c) => c.render), 1);
  const contentH = (maxRender - 1) * NODE_GAP;

  const nodes = laid.map((col, ci) => {
    const colH = (col.render - 1) * NODE_GAP;
    const yStart = (contentH - colH) / 2;
    const x = ci * COL_GAP;
    const pts = [];
    for (let n = 0; n < col.render; n += 1) {
      pts.push({
        x,
        y: yStart + n * NODE_GAP,
        ellipsis: n === col.midEllipsis,
        dropped: col.dropped.has(n),
      });
    }
    return { ...col, x, pts };
  });

  const edges = [];
  for (let ci = 0; ci < nodes.length - 1; ci += 1) {
    const a = nodes[ci];
    const b = nodes[ci + 1];
    a.pts.forEach((pa) => {
      if (pa.ellipsis) return;
      b.pts.forEach((pb) => {
        if (pb.ellipsis) return;
        const r = rand();
        const muted = pa.dropped;
        edges.push({
          x1: pa.x, y1: pa.y, x2: pb.x, y2: pb.y,
          color: lerpColor(a.color, b.color, 0.5),
          width: 0.4 + r * r * 2.0,
          opacity: muted ? 0.04 : 0.06 + r * 0.42,
          dashed: muted,
          a: ci, b: ci + 1,
        });
      });
    });
  }

  const width = (cols.length - 1) * COL_GAP;
  return { nodes, edges, width, height: contentH };
};

const NeuralNetGraph = ({
  layers,
  inputSize = null,        // num features of the chosen segment (real input width)
  outputLabels = null,     // num labels (output channels)
  forecastHorizon = null,  // forecast steps -> output head = horizon x labels
  height = 480,
}) => {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [hoverCol, setHoverCol] = useState(null);
  const drag = useRef(null);
  const sizeRef = useRef({ w: 0, h: height });

  const seed = useMemo(
    () => hashString(JSON.stringify([(layers || []).map((l) => [l.type, l.units, l.filters, l.rate]), inputSize, outputLabels, forecastHorizon])),
    [layers, inputSize, outputLabels, forecastHorizon],
  );
  const { cols, totalParams } = useMemo(
    () => buildArchitecture(theme, layers || [], { inputSize, outputLabels, forecastHorizon }),
    [theme, layers, inputSize, outputLabels, forecastHorizon],
  );
  const scene = useMemo(() => buildScene(cols, seed), [cols, seed]);

  const fit = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const cw = el.clientWidth; const ch = el.clientHeight;
    sizeRef.current = { w: cw, h: ch };
    const W = scene.width + PAD * 2; const H = scene.height + PAD * 2;
    const k = clamp(Math.min(cw / W, ch / H), MIN_K, 1.3);
    setView({ x: (cw - scene.width * k) / 2, y: (ch - scene.height * k) / 2, k });
  }, [scene.width, scene.height]);

  useEffect(() => { fit(); }, [fit]);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => fit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [fit]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left; const my = e.clientY - rect.top;
      setView((v) => {
        const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
        const k2 = clamp(v.k * factor, MIN_K, MAX_K);
        const ratio = k2 / v.k;
        return { k: k2, x: mx - (mx - v.x) * ratio, y: my - (my - v.y) * ratio };
      });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current) return;
    const dx = e.clientX - drag.current.sx; const dy = e.clientY - drag.current.sy;
    setView((v) => ({ ...v, x: drag.current.vx + dx, y: drag.current.vy + dy }));
  };
  const onPointerUp = (e) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };
  const zoomBy = (factor) => {
    const { w, h } = sizeRef.current;
    const mx = w / 2; const my = h / 2;
    setView((v) => {
      const k2 = clamp(v.k * factor, MIN_K, MAX_K);
      const ratio = k2 / v.k;
      return { k: k2, x: mx - (mx - v.x) * ratio, y: my - (my - v.y) * ratio };
    });
  };

  const isDark = theme.palette.mode === 'dark';
  const gridDot = alpha(theme.palette.text.primary, isDark ? 0.10 : 0.07);
  const nodeStroke = theme.palette.background.paper;
  const labelColor = theme.palette.text.secondary;
  const subColor = theme.palette.text.disabled;
  const transform = `translate(${view.x} ${view.y}) scale(${view.k})`;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative', width: '100%', height, borderRadius: 2,
        overflow: 'hidden', border: `1px solid ${theme.palette.divider}`,
        bgcolor: isDark ? alpha(theme.palette.common.black, 0.25) : alpha(theme.palette.background.default, 0.6),
        cursor: drag.current ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none',
      }}
    >
      <svg
        width="100%" height="100%"
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        style={{ display: 'block' }}
      >
        <defs>
          <pattern
            id="nn-grid" width={26} height={26} patternUnits="userSpaceOnUse"
            patternTransform={`translate(${view.x % 26} ${view.y % 26})`}
          >
            <circle cx={1.5} cy={1.5} r={1.2} fill={gridDot} />
          </pattern>
        </defs>

        <rect x={0} y={0} width="100%" height="100%" fill="url(#nn-grid)" />

        <g transform={transform}>
          {/* Edges */}
          <g fill="none" strokeLinecap="round">
            {scene.edges.map((e, i) => (
              <path
                key={i}
                d={`M ${e.x1} ${e.y1} C ${e.x1 + COL_GAP * 0.5} ${e.y1}, ${e.x2 - COL_GAP * 0.5} ${e.y2}, ${e.x2} ${e.y2}`}
                stroke={e.color}
                strokeWidth={e.width}
                strokeDasharray={e.dashed ? '3 4' : undefined}
                strokeOpacity={
                  hoverCol == null ? e.opacity
                    : ((e.a === hoverCol || e.b === hoverCol) ? Math.min(1, e.opacity + 0.4) : e.opacity * 0.12)
                }
              />
            ))}
          </g>

          {/* Columns: nodes + recurrence loops + labels */}
          {scene.nodes.map((col, ci) => {
            const dim = hoverCol != null && hoverCol !== ci;
            const paramStr = col.params > 0 ? `${fmtNum(col.params)} params` : null;
            const tip = [col.title, col.sub, paramStr,
              col.modifiers.map((m) => m.label).join(' · ') || null]
              .filter(Boolean).join('\n');
            return (
              <g
                key={col.key}
                onMouseEnter={() => setHoverCol(ci)}
                onMouseLeave={() => setHoverCol(null)}
              >
                <title>{tip}</title>

                {/* labels above the column */}
                <text x={col.x} y={-PAD * 0.62} textAnchor="middle" fontSize={13}
                      fontWeight={700} fill={hoverCol === ci ? col.color : labelColor}
                      style={{ pointerEvents: 'none' }}>
                  {col.title}
                </text>
                {col.sub && (
                  <text x={col.x} y={-PAD * 0.62 + 16} textAnchor="middle" fontSize={11}
                        fill={subColor} style={{ pointerEvents: 'none' }}>
                    {col.sub}
                  </text>
                )}
                {/* modifier chips below labels */}
                {col.modifiers.length > 0 && (
                  <text x={col.x} y={-PAD * 0.62 + 31} textAnchor="middle" fontSize={10}
                        fill={col.color} fillOpacity={0.85} style={{ pointerEvents: 'none' }}>
                    {col.modifiers.map((m) => m.label).join(' · ')}
                  </text>
                )}

                {/* recurrence self-loops */}
                {col.recurrent && col.pts.map((p, ni) => (
                  p.ellipsis ? null : (
                    <path
                      key={`loop${ni}`}
                      d={`M ${p.x - 3} ${p.y - NODE_R} C ${p.x - 17} ${p.y - NODE_R - 19}, ${p.x + 17} ${p.y - NODE_R - 19}, ${p.x + 3} ${p.y - NODE_R}`}
                      fill="none" stroke={col.color}
                      strokeWidth={1.3}
                      strokeOpacity={dim ? 0.18 : 0.55}
                    />
                  )
                ))}

                {/* nodes */}
                {col.pts.map((p, ni) => (
                  p.ellipsis ? (
                    <text key={ni} x={p.x} y={p.y + 4} textAnchor="middle"
                          fontSize={16} fontWeight={700} fill={subColor}
                          style={{ pointerEvents: 'none' }}>
                      ⋮
                    </text>
                  ) : (
                    <circle
                      key={ni} cx={p.x} cy={p.y} r={NODE_R}
                      fill={p.dropped ? 'none' : col.color}
                      stroke={p.dropped ? col.color : nodeStroke}
                      strokeWidth={1.5}
                      strokeDasharray={p.dropped ? '2 2' : undefined}
                      opacity={dim ? 0.4 : (p.dropped ? 0.6 : 1)}
                    />
                  )
                ))}

                {/* exact-count tag for truncated columns */}
                {col.truncated && (
                  <text x={col.x} y={(scene.height) / 2 + (col.render * NODE_GAP) / 2 + 18}
                        textAnchor="middle" fontSize={11} fontWeight={600}
                        fill={hoverCol === ci ? col.color : subColor}
                        style={{ pointerEvents: 'none' }}>
                    {col.trueCount.toLocaleString()} units
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      {/* total params badge */}
      {totalParams > 0 && (
        <Box
          sx={{
            position: 'absolute', left: 12, top: 10,
            px: 1, py: 0.4, borderRadius: 1,
            fontSize: 11, fontWeight: 600,
            color: 'text.secondary',
            bgcolor: alpha(theme.palette.background.paper, 0.85),
            backdropFilter: 'blur(4px)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          ≈ {fmtNum(totalParams)} trainable params
        </Box>
      )}

      {/* controls */}
      <Box
        sx={{
          position: 'absolute', right: 10, bottom: 10,
          display: 'flex', flexDirection: 'column', gap: 0.5,
          bgcolor: alpha(theme.palette.background.paper, 0.85),
          backdropFilter: 'blur(4px)', borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`, p: 0.5,
        }}
      >
        <Tooltip title="Zoom in" placement="left" arrow>
          <IconButton size="small" onClick={() => zoomBy(1.2)}><AddIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Zoom out" placement="left" arrow>
          <IconButton size="small" onClick={() => zoomBy(1 / 1.2)}><RemoveIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
        <Tooltip title="Fit to view" placement="left" arrow>
          <IconButton size="small" onClick={fit}><CenterFocusStrongIcon sx={{ fontSize: 18 }} /></IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ position: 'absolute', left: 12, bottom: 10, fontSize: 11, color: 'text.disabled', pointerEvents: 'none' }}>
        drag to pan · scroll to zoom
      </Box>
    </Box>
  );
};

export default NeuralNetGraph;
